// Job Alerts page — signed-in users see their alert list, anyone can create one.
// URL-based verify (#/alerts?verify=<token>) and unsubscribe (#/alerts?unsub=<token>) flows.
import React, { useEffect, useState, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import {
    X, Bell, Trash2, MapPin, Sparkles, ShieldCheck, CheckCircle2,
    Clock3, Send, LocateFixed,
} from "lucide-react";
import { CATEGORIES, categoryFor } from "../lib/categories";
import { CategoryIcon } from "../lib/categoryIcons";
import { geocodeSearch } from "../lib/api";
import { createAlert, listAlerts, deleteAlert, verifyAlert, unsubscribeAlert } from "../lib/alertsApi";
import { useAuth } from "../lib/auth";

function detectTimezone() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York";
    } catch {
        return "America/New_York";
    }
}

export default function JobAlertsPage({ onClose, onToast }) {
    const { user, loading: authLoading } = useAuth();
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [banner, setBanner] = useState(null); // { kind: 'verified'|'unsubscribed'|'error', msg }

    // Create-alert form state
    const [email, setEmail] = useState("");
    const [cat, setCat] = useState("");
    const [city, setCity] = useState("");
    const [keyword, setKeyword] = useState("");
    const [radiusKm, setRadiusKm] = useState(25);
    const [useLocation, setUseLocation] = useState(false);
    const [locLatLng, setLocLatLng] = useState(null);
    const [locLabel, setLocLabel] = useState("");
    const [locSearching, setLocSearching] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [formErr, setFormErr] = useState("");
    const [formSuccess, setFormSuccess] = useState("");
    const geoTimer = useRef(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            if (user) {
                const list = await listAlerts();
                setAlerts(list);
            } else {
                setAlerts([]);
            }
        } catch {
            /* ignore */
        } finally {
            setLoading(false);
        }
    }, [user]);

    // Prefill email when signed-in
    useEffect(() => {
        if (user?.email) setEmail(user.email);
    }, [user?.email]);

    // Handle verify / unsubscribe tokens in URL
    useEffect(() => {
        const hash = window.location.hash || "";
        const vm = hash.match(/verify=([^&]+)/);
        const um = hash.match(/unsub=([^&]+)/);
        (async () => {
            if (vm) {
                try {
                    await verifyAlert(decodeURIComponent(vm[1]));
                    setBanner({ kind: "verified", msg: "Alert confirmed — you'll start receiving daily digests at 8am." });
                } catch {
                    setBanner({ kind: "error", msg: "That confirmation link is no longer valid." });
                }
                window.history.replaceState(null, "", window.location.pathname + "#/alerts");
            } else if (um) {
                try {
                    await unsubscribeAlert(decodeURIComponent(um[1]));
                    setBanner({ kind: "unsubscribed", msg: "You've been unsubscribed from that alert." });
                } catch {
                    setBanner({ kind: "error", msg: "That unsubscribe link is no longer valid." });
                }
                window.history.replaceState(null, "", window.location.pathname + "#/alerts");
            }
        })();
    }, []);

    useEffect(() => {
        if (authLoading) return;
        load();
    }, [authLoading, load]);

    // Geocode as user types city (for the "use this location" chip)
    useEffect(() => {
        if (!useLocation || !city.trim() || city.trim().length < 3) {
            return;
        }
        clearTimeout(geoTimer.current);
        setLocSearching(true);
        geoTimer.current = setTimeout(async () => {
            const results = await geocodeSearch(city.trim());
            if (results && results.length > 0) {
                setLocLatLng({ lat: results[0].lat, lng: results[0].lng });
                setLocLabel((results[0].label || "").split(",").slice(0, 3).join(", "));
            } else {
                setLocLatLng(null);
                setLocLabel("");
            }
            setLocSearching(false);
        }, 500);
        return () => clearTimeout(geoTimer.current);
    }, [city, useLocation]);

    const handleUseMyLocation = () => {
        if (!navigator.geolocation) {
            setFormErr("Geolocation not available on this device.");
            return;
        }
        setLocSearching(true);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                setLocLatLng({ lat: pos.coords.latitude, lng: pos.coords.longitude });
                setLocLabel("your current location");
                setUseLocation(true);
                setLocSearching(false);
            },
            () => {
                setFormErr("Couldn't get your location. Try typing a city instead.");
                setLocSearching(false);
            },
            { enableHighAccuracy: false, timeout: 8000 },
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormErr("");
        setFormSuccess("");
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setFormErr("Enter a valid email address.");
            return;
        }
        if (!cat && !city.trim() && !keyword.trim()) {
            setFormErr("Add at least one filter (category, city, or keyword) so we can match jobs.");
            return;
        }
        const filters = {
            category: cat || null,
            city: city.trim() || null,
            keyword: keyword.trim() || null,
            location: (useLocation && locLatLng)
                ? { lat: locLatLng.lat, lng: locLatLng.lng, radius_km: Number(radiusKm) || 25 }
                : null,
        };
        setSubmitting(true);
        try {
            const r = await createAlert({
                email: email.trim(),
                filters,
                timezone: detectTimezone(),
            });
            if (r.verification_required) {
                setFormSuccess(`Check your inbox at ${email} — click the confirmation link to activate this alert.`);
            } else {
                setFormSuccess("Alert saved! You'll receive your first digest tomorrow at 8am.");
                onToast && onToast("Job alert created 🔔");
            }
            // Reset form
            setCat("");
            setCity("");
            setKeyword("");
            setRadiusKm(25);
            setUseLocation(false);
            setLocLatLng(null);
            setLocLabel("");
            await load();
        } catch (err) {
            setFormErr(err?.response?.data?.detail || "Couldn't create alert — please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (alertId) => {
        if (!window.confirm("Remove this alert? You'll stop receiving daily digests for it.")) return;
        try {
            await deleteAlert(alertId);
            setAlerts((a) => a.filter((x) => x.alert_id !== alertId));
            onToast && onToast("Alert removed.");
        } catch {
            onToast && onToast("Couldn't remove that alert.");
        }
    };

    return (
        <div className="fixed inset-0 z-[600] bg-white overflow-auto" data-testid="job-alerts-page">
            <div className="max-w-3xl mx-auto p-5 md:p-8">
                <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                    <div>
                        <div className="inline-flex items-center gap-2 bg-[#fff2ea] text-[#e85812] px-3 py-1 rounded-full text-[11px] font-display font-bold uppercase tracking-wide">
                            <Bell className="w-3.5 h-3.5" /> Job Alerts
                        </div>
                        <h1 className="font-display font-extrabold text-2xl md:text-3xl text-[#1a1e2c] mt-2">
                            Daily digest at 8am, tailored to you
                        </h1>
                        <p className="text-sm text-slate-500 mt-1">
                            One email per day with new hourly jobs matching your filters. Employer-featured jobs get priority.
                        </p>
                    </div>
                    <button className="btn-ghost" onClick={onClose} data-testid="alerts-close-btn">
                        <X className="w-4 h-4" /> Close
                    </button>
                </div>

                {banner && (
                    <div
                        className={`card-soft p-4 mb-5 flex items-start gap-3 ${
                            banner.kind === "error"
                                ? "bg-red-50 border-l-4 border-red-400"
                                : "bg-emerald-50 border-l-4 border-emerald-500"
                        }`}
                        data-testid="alerts-banner"
                    >
                        {banner.kind === "error" ? (
                            <X className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                        ) : (
                            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                        )}
                        <div className="text-[13.5px] font-body text-[#1a1e2c]">{banner.msg}</div>
                    </div>
                )}

                <form
                    onSubmit={handleSubmit}
                    className="card-soft p-5 md:p-6 mb-6 space-y-4"
                    data-testid="alerts-create-form"
                >
                    <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-[#e85812]" />
                        <div className="font-display font-bold text-lg text-[#1a1e2c]">Create a new alert</div>
                    </div>

                    <div className="m3-field">
                        <input
                            id="al-email"
                            type="email"
                            placeholder=" "
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={!!user}
                            data-testid="alerts-email-input"
                            autoComplete="email"
                        />
                        <label htmlFor="al-email">Email address</label>
                        {user && (
                            <div className="text-[11.5px] text-slate-500 mt-1 flex items-center gap-1.5">
                                <ShieldCheck className="w-3 h-3 text-emerald-600" />
                                Signed in — auto-verified.
                            </div>
                        )}
                    </div>

                    <div>
                        <div className="text-[12px] font-display font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Category (optional)</div>
                        <div className="flex flex-wrap gap-2">
                            <button
                                type="button"
                                onClick={() => setCat("")}
                                className={`px-3 py-1.5 rounded-full text-[12.5px] font-display font-semibold transition-all ${
                                    !cat ? "bg-[#e85812] text-white shadow-lg" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                                }`}
                                data-testid="alerts-cat-any"
                            >
                                Any category
                            </button>
                            {CATEGORIES.map((c) => (
                                <button
                                    key={c.id}
                                    type="button"
                                    onClick={() => setCat(c.id)}
                                    className={`px-3 py-1.5 rounded-full text-[12.5px] font-display font-semibold inline-flex items-center gap-1.5 transition-all ${
                                        cat === c.id
                                            ? "bg-[#e85812] text-white shadow-lg"
                                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                                    }`}
                                    data-testid={`alerts-cat-${c.id}`}
                                >
                                    <CategoryIcon catId={c.id} size={14} />
                                    {c.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="m3-field">
                            <input
                                id="al-city"
                                type="text"
                                placeholder=" "
                                value={city}
                                onChange={(e) => {
                                    setCity(e.target.value);
                                    setLocLatLng(null);
                                    setLocLabel("");
                                }}
                                data-testid="alerts-city-input"
                            />
                            <label htmlFor="al-city">City (optional)</label>
                        </div>
                        <div className="m3-field">
                            <input
                                id="al-kw"
                                type="text"
                                placeholder=" "
                                value={keyword}
                                onChange={(e) => setKeyword(e.target.value)}
                                data-testid="alerts-keyword-input"
                            />
                            <label htmlFor="al-kw">Keyword (title/company)</label>
                        </div>
                    </div>

                    <div className="bg-slate-50 rounded-2xl p-4 space-y-3">
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                            <input
                                type="checkbox"
                                checked={useLocation}
                                onChange={(e) => {
                                    setUseLocation(e.target.checked);
                                    if (!e.target.checked) {
                                        setLocLatLng(null);
                                        setLocLabel("");
                                    }
                                }}
                                className="accent-[#e85812] w-4 h-4"
                                data-testid="alerts-use-location-toggle"
                            />
                            <MapPin className="w-4 h-4 text-[#e85812]" />
                            <span className="text-[13.5px] font-display font-semibold text-[#1a1e2c]">
                                Only within a distance of a location
                            </span>
                        </label>
                        {useLocation && (
                            <div className="pl-6 space-y-3">
                                <div className="text-[12px] text-slate-500">
                                    {locLatLng
                                        ? <>Anchored to <b>{locLabel || `${locLatLng.lat.toFixed(3)}, ${locLatLng.lng.toFixed(3)}`}</b></>
                                        : (locSearching ? "Finding location…" : "Type a city above or use my current location.")}
                                </div>
                                <button
                                    type="button"
                                    onClick={handleUseMyLocation}
                                    className="btn-ghost !py-1.5 !px-3 !text-xs"
                                    data-testid="alerts-use-my-location-btn"
                                >
                                    <LocateFixed className="w-3.5 h-3.5" /> Use my current location
                                </button>
                                <div>
                                    <div className="text-[12px] font-display font-semibold text-slate-600 mb-1">
                                        Within <span className="text-[#e85812] font-bold">{radiusKm} km</span> of that location
                                    </div>
                                    <input
                                        type="range"
                                        min={5}
                                        max={150}
                                        step={5}
                                        value={radiusKm}
                                        onChange={(e) => setRadiusKm(e.target.value)}
                                        className="w-full accent-[#e85812]"
                                        data-testid="alerts-radius-slider"
                                    />
                                    <div className="flex justify-between text-[10.5px] text-slate-400">
                                        <span>5 km</span><span>75 km</span><span>150 km</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {formErr && <div className="text-[13px] text-red-600" data-testid="alerts-error">{formErr}</div>}
                    {formSuccess && (
                        <div className="text-[13px] text-emerald-700 bg-emerald-50 p-3 rounded-xl" data-testid="alerts-success">
                            {formSuccess}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={submitting}
                        className="btn-primary w-full !py-3 !text-[15px]"
                        data-testid="alerts-submit-btn"
                    >
                        <Send className="w-4 h-4" />
                        {submitting ? "Creating…" : "Create alert"}
                    </button>
                </form>

                {user && (
                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <div className="font-display font-bold text-lg text-[#1a1e2c]">
                                Your active alerts ({alerts.length})
                            </div>
                        </div>
                        {loading && <div className="text-slate-500 text-sm">Loading…</div>}
                        {!loading && alerts.length === 0 && (
                            <div className="card-soft p-6 text-center text-slate-500 text-sm" data-testid="alerts-empty">
                                No active alerts yet. Create your first one above 🔔
                            </div>
                        )}
                        <div className="space-y-2">
                            {alerts.map((a) => {
                                const f = a.filters || {};
                                const catObj = f.category ? categoryFor(f.category) : null;
                                return (
                                    <motion.div
                                        key={a.alert_id}
                                        initial={{ opacity: 0, y: 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="card-soft p-4 flex items-center gap-3"
                                        data-testid={`alert-row-${a.alert_id}`}
                                    >
                                        <span
                                            className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
                                            style={{ background: catObj ? catObj.color : "#ffdccb" }}
                                        >
                                            {catObj ? <CategoryIcon catId={catObj.id} size={20} /> : <Bell className="w-5 h-5 text-[#e85812]" />}
                                        </span>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-display font-semibold text-[15px] text-[#1a1e2c] truncate">
                                                {catObj ? catObj.label : "All categories"}
                                                {f.city && <span className="text-slate-500 font-normal"> · {f.city}</span>}
                                                {f.keyword && <span className="text-slate-500 font-normal"> · &quot;{f.keyword}&quot;</span>}
                                            </div>
                                            <div className="text-[12px] text-slate-500 flex items-center gap-2 mt-0.5 flex-wrap">
                                                {a.verified ? (
                                                    <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-3 h-3" /> verified</span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 text-amber-700"><Clock3 className="w-3 h-3" /> pending confirmation</span>
                                                )}
                                                <span>· 8am {a.timezone || "local"}</span>
                                                {f.location?.radius_km && <span>· within {Math.round(f.location.radius_km)} km</span>}
                                            </div>
                                        </div>
                                        <button
                                            className="btn-ghost !py-2 !px-2 text-red-500"
                                            onClick={() => handleDelete(a.alert_id)}
                                            data-testid={`alert-delete-${a.alert_id}`}
                                            aria-label="delete alert"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {!user && (
                    <div className="text-center text-[12.5px] text-slate-500 mt-2">
                        <b>Sign in with Google</b> to see and manage all your alerts in one place.
                    </div>
                )}
            </div>
        </div>
    );
}
